import { Injectable, signal } from '@angular/core';

import { environment } from 'src/environments/environment';

import { Apps, AppsLabel } from '../models/app-switcher.enum';
import { AppSwitcherItem } from '../models/app-switcher.state';
import { UserApp } from '../models/company-api.models';

/** Maps each app ID to its display tile. URLs come from environment, logos from local assets. */
function buildAppCatalog(): Partial<Record<Apps, AppSwitcherItem>> {
  return {
    [Apps.TPMPortal]: {
      id: Apps.TPMPortal,
      label: AppsLabel[Apps.TPMPortal],
      logo: 'assets/applogos/tpmlogo.svg',
      url: environment.tpmUrl
    },
    [Apps.POS]: {
      id: Apps.POS,
      label: AppsLabel[Apps.POS],
      logo: 'assets/applogos/poslogo.svg',
      url: environment.posUrl
    },
    [Apps.Donation]: {
      id: Apps.Donation,
      label: AppsLabel[Apps.Donation],
      logo: 'assets/applogos/donationlogo.svg',
      url: environment.donationUrl
    },
    [Apps.Lister]: {
      id: Apps.Lister,
      label: AppsLabel[Apps.Lister],
      logo: 'assets/applogos/listerlogo.svg',
      url: environment.listerUrl
    },
    [Apps.CompanyPortal]: {
      id: Apps.CompanyPortal,
      label: AppsLabel[Apps.CompanyPortal],
      logo: 'assets/applogos/brijjlogo.svg',
      url: environment.companyUrl
    },
    [Apps.Inventory]: {
      id: Apps.Inventory,
      label: AppsLabel[Apps.Inventory],
      logo: 'assets/applogos/imslogo.svg',
      url: environment.imsUrl
    }
  };
}

/**
 * Holds the app-switcher tile list. Populated by `AppComponent` from the same
 * Company API `Users/{id}` response it already fetches for display name/store
 * resolution — this service does not perform its own HTTP call.
 */
@Injectable({ providedIn: 'root' })
export class AppSwitcherService {
  private readonly _apps = signal<AppSwitcherItem[]>([]);

  /** Apps available to the current user (excludes Consignment since we're already in it). */
  readonly apps = this._apps.asReadonly();

  /** Resolves the raw `apps` array from `UserDetailsResponse` into display tiles. */
  setApps(userApps: UserApp[]): void {
    const catalog = buildAppCatalog();
    const items = userApps
      .map(a => a.id)
      .filter(id => id !== Apps.Consignment && catalog[id as Apps])
      .map(id => catalog[id as Apps]!);
    this._apps.set(items);
  }
}
